import React, { createContext, useContext, useEffect, useState } from "react";
import { CDNFileData, CDN_FILE_API_ENDPOINTS, defaultCDNFileData } from "./constants";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../utils/axiosInstance";
import { message } from "antd";

interface CDNFileContextState {
	cdnData: CDNFileData;
	loadingCDNFileData?: boolean;
	loadingAddCDNFileData: boolean;
	loadingGetCDNFileData?: boolean;
	loading: boolean;
	activeSameImages: boolean;
	loadingEditCDNFileData: boolean;
	setCDNFileData: React.Dispatch<React.SetStateAction<CDNFileData>>;
	addCDNFile: () => void;
	updateCDNFile: () => void;
	getCDNFileDetails: (id: string) => void;
	clearCDNFileData: () => void;
	setActiveSameImages: React.Dispatch<React.SetStateAction<boolean>>;
}

interface CDNFileProviderProps {
	children: React.ReactNode;
}

const CDNFileContext = createContext<CDNFileContextState | undefined>(
	undefined
);

export const useCDNFileContext = () => {
	const context = useContext(CDNFileContext);
	if (!context) {
		throw new Error(
			"useCDNFileContext must be used within an CDNFileContextProvider"
		);
	}
	return context;
};

function isFileObject(value: any) {
	return value instanceof File;
}

export const CDNFileContextProvider: React.FC<CDNFileProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [cdnData, setCDNFileData] = useState<CDNFileData>(defaultCDNFileData);
	const [activeSameImages, setActiveSameImages] = useState<boolean>(false);

	const [loadingAddCDNFileData, setLoadingAddCDNFileData] = useState(false);
	const [loadingEditCDNFileData, setLoadingEditCDNFileData] = useState(false);
	const [loadingGetCDNFileData, setLoadingGetCDNFileData] = useState(false);
	const [errorAddCDNFileData, setErrorAddCDNFileData] = useState<any>(undefined);

	const loading = loadingAddCDNFileData;
	const error = errorAddCDNFileData;

	const isCDNFileDataValid = (data: CDNFileData) => {
		if (!data.releaseDate || data.isVisible === undefined || data.displayOrder === undefined || data.title === undefined || !data.imageUrl) {
			return false;
		}

		return true;
	};

	const clearCDNFileData = () => {
		setCDNFileData(defaultCDNFileData);
	};

	const addCDNFile = async () => {
		setLoadingAddCDNFileData(true);

		if (!isCDNFileDataValid(cdnData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setLoadingAddCDNFileData(false);
			return;
		}

		const formData = new FormData();
		formData.append("releaseDate", cdnData.releaseDate);
		formData.append("isVisible", cdnData.isVisible.toString());
		formData.append("displayOrder", cdnData.displayOrder?.toString() ?? "");
		formData.append("title", cdnData.title);

		if (cdnData.imageUrl !== null && isFileObject(cdnData.imageUrl)) {
			formData.append("imageUrl", cdnData.imageUrl);
		}

		try {
			const response = await axiosInstance.post(`${CDN_FILE_API_ENDPOINTS.ADD_CDN_FILE}`, formData, {
				headers: {
					"Content-Type": "multipart/form-data"
				}
			});

			setLoadingAddCDNFileData(false);

			if (response.data && response.data.result.isSuccessful) {
				message.success("CDN Dosyası başarıyla eklendi.");
				setCDNFileData(defaultCDNFileData);
				navigate("/cdn");
			} else {
				message.error(response.data.result.error.message);
			}
		} catch (error) {
			console.error(error);
			setErrorAddCDNFileData(error);
			setLoadingAddCDNFileData(false);
		}
	};

	const updateCDNFile = async () => {
		setLoadingEditCDNFileData(true);

		if (!isCDNFileDataValid(cdnData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setLoadingEditCDNFileData(false);
			return;
		}

		const formData = new FormData();
		formData.append("releaseDate", cdnData.releaseDate);
		formData.append("isVisible", cdnData.isVisible.toString());
		formData.append("displayOrder", cdnData.displayOrder?.toString() ?? "");
		formData.append("cdnFileId", cdnData.cdnFileId?.toString() ?? "");
		formData.append("title", cdnData.title);

		if (cdnData.imageUrl) {
			formData.append("imageUrl", cdnData.imageUrl);
		}

		try {
			const response = await axiosInstance.put(`${CDN_FILE_API_ENDPOINTS.UPDATE_CDN_FILE}`, formData, {
				headers: {
					"Content-Type": "multipart/form-data"
				}
			});

			setLoadingEditCDNFileData(false);

			if (response.data && response.data.result.isSuccessful) {
				message.success("CDN Dosyası başarıyla güncellendi.");
				setCDNFileData(defaultCDNFileData);
				navigate("/cdn");
			} else {
				message.error(response.data.result.error.message);
			}
		} catch (error) {
			console.error(error);
			setErrorAddCDNFileData(error);
			setLoadingEditCDNFileData(false);
		}
	};

	const getCDNFileDetails = async (id: string) => {
		setLoadingGetCDNFileData(true);
		try {
			const response = await axiosInstance.get(`${CDN_FILE_API_ENDPOINTS.GET_CDN_FILE}?cdnFileId=${id}`);
			if (response.data && response.data.isSuccessful) {
				const result = response?.data?.result;
				// Map backend property names to frontend
				setCDNFileData({
					releaseDate: result.releaseDate,
					isVisible: result.isVisible,
					displayOrder: result.displayOrder,
					title: result.title,
					imageUrl: result.imageUrl,
					cdnFileId: result.cdnFileId
				});
			}
			setLoadingGetCDNFileData(false);
		} catch (error) {
			console.error(error);
			setErrorAddCDNFileData(error);
			setLoadingGetCDNFileData(false);
		}
	};

	const value: CDNFileContextState = {
		cdnData,
		loadingGetCDNFileData,
		loadingAddCDNFileData,
		loadingEditCDNFileData,
		loading,
		activeSameImages,
		setCDNFileData,
		addCDNFile,
		clearCDNFileData,
		updateCDNFile,
		getCDNFileDetails,
		setActiveSameImages,
	};

	return (
		<CDNFileContext.Provider value={value}>
			{children}
		</CDNFileContext.Provider>
	);
};